/**
 * Gate de celebração — o registro de "agora NÃO".
 *
 * ## O problema que ele resolve
 *
 * O cartão de conquista é disparado por um watcher GLOBAL (`BadgeUnlockWatcher`
 * em `AppRoutes`), e watcher global não sabe o que está aberto na tela. Sem
 * gate, o ganho de uma ofensiva abriria um cartão por cima de uma sessão de
 * Foco rodando — a lição da família BUG-62/67/68 — ou por cima do modal de
 * conclusão de livro, que já está celebrando outra coisa.
 *
 * ## Por que registro explícito e não varredura do DOM
 *
 * A casa não tem registro de overlay aberto, e adivinhar pelo z-index
 * (`.fixed.inset-0.z-50`) quebra no primeiro refactor de classe. Aqui cada
 * superfície crítica declara o bloqueio enquanto está montada, com um motivo
 * nomeado — quando alguém quebrar, o bug aponta para a linha.
 *
 * ## Quem bloqueia hoje
 *
 * `focus` (FocusModeModal, inclusive minimizado — a sessão segue viva),
 * `completion` (BookCompletionModal), `badge-share` (Studio aberto a partir de
 * /badges), `league-recap` (a outra celebração automática — o cartão
 * de fechamento da semana de liga),
 * `badge-unlock` (o próprio cartão, enquanto está aberto), `push-prompt`
 * (o convite de notificações), `review-prompt` (o convite de avaliação nas
 * lojas — que além do gate ainda ESPERA o push ser resolvido, ver
 * `ReviewPromptWatcher`), `league-intro` (a apresentação da Liga na
 * primeira visita — `/leagues` está na allowlist de celebração, então sem o
 * bloqueio o cartão de conquista abriria por cima dela) e `streak-recovery`
 * (o modal automático de "sua ofensiva quebrou", disparado por `useHomeState`
 * assim que o perfil carrega — mesmo gatilho de boot do badge-unlock e do
 * push-prompt, e sem o bloqueio os três podiam abrir empilhados na Home).
 *
 * ⚠️ Os dois últimos existem porque o gate deixou de ser só "não celebre agora"
 * e virou o registro de **overlay automático na tela**: o cartão de conquista e
 * o convite de push nascem dos mesmos gatilhos (primeira rota segura depois do
 * login) e empilhariam um sobre o outro. Quem chega primeiro bloqueia; o outro
 * espera. Não há deadlock: o bloqueio é lido só na DECISÃO de abrir — quem já
 * está aberto não se relê.
 *
 * Onboarding e checkout NÃO passam por aqui: são rotas próprias, e o gate de
 * rota em `useBadgeUnlockQueue` já as exclui — um bloqueio a menos para manter.
 *
 * ⚠️ `StudioShellV3` não é tocado de propósito (congelado pela Onda B). Os
 * call sites que o abrem é que registram.
 */

import { useEffect } from "react";

export type CelebrationBlockReason =
  /** O cartão "Dia 1 de leitura" (primeira semana, 07/10) — mesmo gatilho de
   *  boot da Home que o cartão de conquista e a oferta do PRO. */
  | "day-one"
  | "focus"
  | "completion"
  | "badge-share"
  | "league-recap"
  | "badge-unlock"
  | "push-prompt"
  | "review-prompt"
  | "league-intro"
  /** O aviso de fim do PRO (`ProLapseWatcher`) — nasce do mesmo gatilho dos
   *  outros (primeira rota segura) e empilharia com o cartão de conquista. */
  | "pro-lapse"
  /**
   * O card de evento (`BienalCardFlow`) — a folha de números e o Studio.
   *
   * A faixa que o abre mora na HOME, que está na allowlist de celebração: sem
   * este bloqueio, o cartão de conquista ou o convite de push abririam por cima
   * de um formulário que a pessoa está no meio de preencher. Mesmo motivo do
   * `badge-share`.
   */
  | "bienal-card"
  /** O modal (automático ou manual) de recuperação de ofensiva, na Home. */
  | "streak-recovery"
  /**
   * A oferta do PRO depois do onboarding (`OnboardingProOffer`, na Home).
   * Nasce do mesmo gatilho do convite de push (a primeira Home da conta) —
   * sem o bloqueio as duas folhas subiriam empilhadas.
   */
  | "pro-offer"
  /**
   * O cartão "Seu mês está pronto" (Estatísticas v2, 26/09 — `MonthRecapInvite`
   * na Home, dias 1–7). Evento do dia 1º, o mesmo dia do recap da liga: sem o
   * bloqueio, os dois cartões subiriam juntos.
   */
  | "monthly-recap"
  /** O cartão "Seu 2026 está pronto" (Wrap anual, 15–31/12). */
  | "year-wrap";

type Listener = (blocked: boolean) => void;

/**
 * Contagem por motivo, não Set de motivos: duas instâncias do mesmo overlay
 * podem coexistir por um frame durante a troca de rota, e a que desmonta não
 * pode liberar o bloqueio da que montou.
 */
const blocks = new Map<CelebrationBlockReason, number>();
const listeners = new Set<Listener>();

function emit(): void {
  const blocked = isCelebrationBlocked();
  for (const l of listeners) l(blocked);
}

export function isCelebrationBlocked(): boolean {
  return blocks.size > 0;
}

/** Motivos ativos — usado em teste e diagnóstico. */
export function getCelebrationBlocks(): CelebrationBlockReason[] {
  return [...blocks.keys()];
}

export function blockCelebrations(reason: CelebrationBlockReason): void {
  blocks.set(reason, (blocks.get(reason) ?? 0) + 1);
  if (blocks.get(reason) === 1) emit();
}

export function releaseCelebrations(reason: CelebrationBlockReason): void {
  const count = blocks.get(reason);
  if (count === undefined) return;
  if (count <= 1) {
    blocks.delete(reason);
    emit();
    return;
  }
  blocks.set(reason, count - 1);
}

export function subscribeCelebrationGate(listener: Listener): () => void {
  listeners.add(listener);
  listener(isCelebrationBlocked());
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Declara o bloqueio enquanto `active` for verdadeiro e o componente estiver
 * montado. A liberação no cleanup é o que garante que um unmount abrupto
 * (navegação, erro) não deixe a fila presa para sempre.
 */
export function useSuppressCelebrations(active: boolean, reason: CelebrationBlockReason): void {
  useEffect(() => {
    if (!active) return;
    blockCelebrations(reason);
    return () => releaseCelebrations(reason);
  }, [active, reason]);
}

/** Só para testes. */
export function __resetCelebrationGateForTests(): void {
  blocks.clear();
  listeners.clear();
}
